import {NgModule} from '@angular/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {RouterModule, Routes, UrlSegment} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {SharedModule} from '../shared/shared.module';

import {TileGroupComponent} from './tile-group/tile-group.component';
import {TileComponent} from './tile/tile.component';
import {BuilderComponent} from './builder.component';
import {PendingChangesDialogComponent} from './pending-changes-dialog/pending-changes-dialog.component';
import {FiltersDialogComponent} from './filters-dialog/filters-dialog.component';
import {SizeSelectorComponent} from './size-selector/size-selector.component';
import {MatSelectorComponent} from './product-selectors/mat-selector/mat-selector.component';
import {ProductSelectorComponent} from './product-selectors/product-selector/product-selector.component';
import {OptionComponent} from './option/option.component';
import {LazySizeSelectComponent} from './lazy-size-select/lazy-size-select.component';
import {ProjectPreviewComponent} from './project-preview/project-preview.component';
import {FiltersComponent} from './filters/filters.component';
import {ChooseSizeDialogComponent} from './choose-size-dialog/choose-size-dialog.component';
import {StandardSizesComponent} from './standard-sizes/standard-sizes.component';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PendingChangesGuard } from './pending-changes.guard';
import { OpeningSizeCalculatorDialogComponent } from './opening-size-calculator-dialog/opening-size-calculator-dialog.component';
import { SizeSelectComponent } from './size-select/size-select.component';
import { PriceAndBuyComponent } from './price-and-buy/price-and-buy.component';
import { SelectorDialogComponent } from './selector-dialog/selector-dialog.component';
import { UploadDialogComponent } from './upload-dialog/upload-dialog.component';
import { ShapeSelectorComponent } from './shape-selector/shape-selector.component';
import { RotationViolationDialogComponent } from './rotation-violation-dialog/rotation-violation-dialog.component';
import {
  customShareButton,
  provideShareButtonsOptions,
  ShareButtonDirective,
  ShareButtonFuncArgs
} from 'ngx-sharebuttons';
import { ZoomDialogComponent } from './zoom-dialog/zoom-dialog.component';
import {environment} from "../../environments/environment";
import {ProjectService} from "../shared/services/project.service";
import {BrowserWindowRef, windowFactory, WindowRef} from "../shared/services/window.service";
import {PinchZoomComponent} from '@meddv/ngx-pinch-zoom';

const routes: Routes = [
  {
    matcher: (url) => {
      const clearNulls = (params): any => {
        return Object.fromEntries(
          Object.entries(params).filter(p => p[1] !== null)
        );
      }

      // samples builder
      if (url[0].path.toLowerCase().startsWith('samples')) {
        return {
          consumed: url,
          posParams: clearNulls({
            primaryProduct: new UrlSegment('SAMPLES', {})
          })
        };
      }

      // predefined project
      if (url[0].path.toLowerCase().startsWith('project')) {
        return {
          consumed: url,
          posParams: clearNulls({
            projectId: new UrlSegment(url[1].path, {}),
            description: url[2] ? new UrlSegment(url[2].path, {}) : null,
          })
        }
      }

      // default links
      if (/^[0-9\.]+x[0-9\.]+/.test(url[0].path)) {
        const matches = /^([0-9\.]+x[0-9\.]+)-(.+)/.exec(url[0].path);
        const retValue = {
          consumed: url,
          posParams: {
            outerSize: new UrlSegment(matches[1], {})
          }
        };

        const outerSize: number[] = matches[1].split('x').map(s => parseFloat(s));
        const mouldingCode = Math.max(outerSize[0], outerSize[1]) > environment.defaultProductProperties.moulding.maxLongDimension
          ? environment.defaultProductProperties.moulding.wide
          : environment.defaultProductProperties.moulding.regular;

        let matchFound = false;
        ProjectService.standardSizes
          .filter( size => size.visible)
          .forEach( size => {
            if (outerSize[0] === size.outer[0] && outerSize[1] === size.outer[1] && size.artwork[0] > 0 && size.artwork[1] > 0) {
              retValue.posParams['openingSize'] = new UrlSegment(`${size.artwork[0] - 0.5} x ${size.artwork[1] - 0.5}`, {});
              matchFound = true;
              return;
            }
          });

        if (!matchFound) {
          const margin = outerSize[0] < 5 || outerSize[1] < 5 ? 2.5 : 5;
          retValue.posParams['openingSize'] = new UrlSegment(`${outerSize[0] - margin} x ${outerSize[1] - margin}`, {});
        }

        if (matches[2].indexOf('double-mat') >= 0) {
          retValue.posParams['topMatCode'] = new UrlSegment(environment.defaultProductProperties.topMat, {});
          retValue.posParams['bottomMatCode'] = new UrlSegment(environment.defaultProductProperties.bottomMat, {});
          retValue.posParams['reveal'] = new UrlSegment(environment.defaultProductProperties.reveal, {});
        } else if (matches[2].indexOf('mat') >= 0) {
          retValue.posParams['topMatCode'] = new UrlSegment(environment.defaultProductProperties.topMat, {});
        }

        if (matches[2].indexOf('frame') >= 0) {
          retValue.posParams['mouldingCode'] = new UrlSegment(mouldingCode, {});
          retValue.posParams['primaryProduct'] = new UrlSegment('FRAME', {});
          delete retValue.posParams['openingSize'];
        } else {
          retValue.posParams['primaryProduct'] = new UrlSegment('MAT', {});
        }

        return retValue;
      }

      // predefined product loader
      if (url[0].path.toLowerCase().startsWith('product')) {
        const id = url[1].path.split('-')[0];
        const retValue = {
          consumed: url,
          posParams: clearNulls({
            description: url[2] ? new UrlSegment(url[2].path, {}) : null,
          })
        }
        retValue.posParams[/^[0-9]+$/.test(id) ? 'predefinedProductId' : 'predefinedProductProjectId'] = new UrlSegment(id, {});
        return retValue;
      }

      // upsell of other parts on an existing project
      if (url[0].path.toLowerCase().startsWith('upsell')) {
        return {
          consumed: url,
          posParams: clearNulls({
            upsellProperty: new UrlSegment(url[1].path, {}),
            projectId: url[2] ? new UrlSegment(url[2].path, {}) : null
          })
        }
      }

      // continuing with an unfinished project
      if (url[0].path.toLowerCase().endsWith('unfinished-order')) {
        return {
          consumed: url,
          posParams: {
            tempProjectId: new UrlSegment('temp', {})
          }
        }
      }

      // used for frame and mat products if present
      const tags = url[1] ? url[1].path.toLowerCase().split('_') : [];

      // frame-first builder
      if (url[0].path.toLowerCase().indexOf('frames') !== -1) {
        return url.length > 1 ? {
          consumed: url,
          posParams: clearNulls({
            primaryProduct: new UrlSegment('FRAME', {}),
            category: tags[0] ? new UrlSegment(tags[0].replace(/\-/g, ' '), {}) : null,
            groupName: tags[0] ? new UrlSegment(tags[0].replace(/\-/g, '_'), {}) : null,
            description: tags[1] ? new UrlSegment(tags[1].replace(/\-/g, ' '), {}) : null,
          })
        } : {
          consumed: url,
          posParams: clearNulls({
            primaryProduct: new UrlSegment('FRAME', {}),
            outerSize: new UrlSegment(environment.defaultProductProperties.outerSize, {}),
            mouldingCode: new UrlSegment(environment.defaultProductProperties.moulding.regular, {})
          })
        }
      }

      // mat-first builder
      if (url[0].path.toLowerCase().indexOf('matboard') !== -1) {
        return url.length > 1 ? {
          consumed: url,
          posParams: clearNulls({
            primaryProduct: new UrlSegment('MAT', {}),
            quality: tags[0] ? new UrlSegment(tags[0], {}) : null,
            core: tags[1] ? new UrlSegment(tags[1].replace('-core', ''), {}) : null,
            thickness: tags[2] ? new UrlSegment(tags[2].replace('-', ' '), {}) : null,
            finish: tags[3] ? new UrlSegment(tags[3], {}) : null
          })
        } : {
          consumed: url,
          posParams: clearNulls({
            primaryProduct: new UrlSegment('MAT', {}),
            outerSize: new UrlSegment(environment.defaultProductProperties.outerSize, {}),
            openingSize: new UrlSegment(environment.defaultProductProperties.openingSize, {}),
            topMatCode: new UrlSegment(environment.defaultProductProperties.topMat, {})
          })
        }
      }

      // project (edit or copy-from)
      if (url.length === 1) {
        return {
          consumed: url,
          posParams: {
            projectId: new UrlSegment(url[0].path, {})
          }
        }
      }

    },
    component: BuilderComponent,
    canDeactivate: [PendingChangesGuard]
  }
];

@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    MatDialogModule,
    MatIconModule, MatInputModule, MatButtonModule, MatButtonToggleModule, MatGridListModule, MatTabsModule,
    MatAutocompleteModule, MatProgressSpinnerModule, MatSelectModule, MatProgressBarModule,
    MatMenuModule, MatListModule, MatCheckboxModule, MatTooltipModule,
    FormsModule,
    PinchZoomComponent, ShareButtonDirective
  ],
  declarations: [
    BuilderComponent, TileGroupComponent, TileComponent, FiltersDialogComponent, PendingChangesDialogComponent,
    SizeSelectorComponent, MatSelectorComponent, ProductSelectorComponent, OptionComponent,
    LazySizeSelectComponent, ProjectPreviewComponent, FiltersComponent,
    ChooseSizeDialogComponent, OpeningSizeCalculatorDialogComponent,
    StandardSizesComponent, SizeSelectComponent, PriceAndBuyComponent, SelectorDialogComponent, UploadDialogComponent,
    ShapeSelectorComponent, RotationViolationDialogComponent, ZoomDialogComponent
  ]
})
export class BuilderSharedModule {}

@NgModule({
  imports: [
    CommonModule,
    BuilderSharedModule,
    RouterModule.forChild(routes)
  ],
  declarations: [
  ],
  providers: [
    PendingChangesGuard,
    provideShareButtonsOptions(
      customShareButton('downloadFile', {
        type: 'download',
        text: 'Download',
        icon: ['fas', 'fas-download'],
        // or if the button needs to execute a function, use the following instead
        func: (args: ShareButtonFuncArgs<any>) => {
          const winRef = new BrowserWindowRef();
          if (winRef && winRef.nativeWindow && winRef.nativeWindow as Window) {
            const window = winRef.nativeWindow as Window;
            const link = window.document.createElement('a');
            link.innerHTML = "download";
            link.setAttribute('type', 'hidden');
            link.href = args.params.url;
            link.download = "download";
            window.document.body.appendChild(link);
            link.click();
            link.remove();
          }
        }
      })
    ),]
})
export class BuilderModule { }
